/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import cn from "classnames";
import { withRouter } from "react-router-dom";
import { isEmpty } from "lodash";
import usePrevious from "../hooks/usePrevious";

import SidebarModal from "./SidebarModal";
import Button from "./Button";
import ConfirmSplitPaymentDialog from "./dialog/ConfirmSplitPaymentDialog";

import styles from "./CartModal.module.scss";
import CartItems from "../containers/CartItems";
import useOfflineModal from "../hooks/useOfflineModal";
import useRemoteOrderTotal from "../hooks/useRemoteOrderTotal";

const CartModal = ({
  history,
  hasCartItems,
  clearCart,
  className,
  cartItems,
  total,
  itemCount,
  discountsAvailable,
  noOpPayments,
  signatureRequired,
  tipSuggestions,
  cashEnabled,
  networkAvailable,
  ticketEnabled,
  mobilePaySetting,
  clearPromotionsOnOrder,
  giftCardEnabled,
}) => {
  const classNames = cn(styles.cartModal, className);
  const [showCart, setShowCart] = useState(false);
  const [showSplitPayDialog, setShowSplitPayDialog] = useState(false);
  const [checkIfOfflineWithCallback, offlineModal] = useOfflineModal()
  const [loadRemoteOrderTotal, { data: remoteOrderTotal, success, failed }] = useRemoteOrderTotal()

  const prevCartItems = usePrevious(cartItems);
  const buttonsDisabled = !hasCartItems;
  const tipEnabled = !isEmpty(tipSuggestions)

  const clearItemsAndPromotions = () => {
    clearCart()
    clearPromotionsOnOrder()
  }

  useEffect(() => {
    if (prevCartItems && !isEmpty(prevCartItems) && isEmpty(cartItems)) {
      setShowCart(false);
    }
  }, [cartItems]);

  useEffect(() => {
    if (success || failed) {
      handleCardTransaction()
    }
  }, [success, failed]);

  const handleCardTransaction = () => {
    if (signatureRequired || tipEnabled) {
      history.push("/signature");
    } else {
      history.push("/card-reader");
    }
  };

  // TODO (ZAID) send to android

  const handleScanScreenSelection = (scanScreenPath) => {
    if (tipEnabled) {
      history.push({
        pathname: '/signature',
        state: {
          addTipThenRedirectPath: scanScreenPath,
        }
      })
      return
    }
    history.push(scanScreenPath)
  }

  const offlineModalScanScreenMessage = 'This payment tender is not supported\nwhile the device is offline'

  const trueButtonCount =
    !!cashEnabled +
    !isEmpty(noOpPayments) +
    !!mobilePaySetting +
    !!ticketEnabled + 1

  let buttonCount;
  if (trueButtonCount > 4) {
    buttonCount = 4;
  } else {
    buttonCount = trueButtonCount;
  }

  const oddButtonCount = buttonCount % 2 === 1;

  const showOtherButton = trueButtonCount > 3;

  let buttons = [
    <Button
      className={cn([
        styles.checkout,
        buttonCount === 1 ? styles.long : styles.short,
      ])}
      disabled={buttonsDisabled}
      onClick={() => loadRemoteOrderTotal()}
    >
      {"Card"}
    </Button>,
  ];

  if (cashEnabled) {
    buttons.push(
      <Button
        className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length+1 ? styles.long : styles.short])}
        onClick={() => history.push("/change-calculator")}
      >
        {"Cash"}
      </Button>
    );
  }

  if (
    (buttons.length < 3 || (buttons.length < 4 && !showOtherButton)) &&
    mobilePaySetting
  ) {
    buttons.push(
      <Button
        className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length+1 ? styles.long : styles.short])}
        onClick={() => {
          if (mobilePaySetting === "pos_shows_qr") {
            history.push("/rich-checkout");
            return;
          }
          checkIfOfflineWithCallback(() => handleScanScreenSelection('/qr-pay'), offlineModalScanScreenMessage, true)
        }}
      >
        {mobilePaySetting === "pos_shows_qr" ? "App" : "QR Pay"}
      </Button>
    );
  }

  if (
    (buttons.length < 3 || (buttons.length < 4 && !showOtherButton)) &&
    ticketEnabled
  ) {
    buttons.push(
      <Button
        className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length+1 ? styles.long : styles.short])}
        onClick={() => checkIfOfflineWithCallback(() => handleScanScreenSelection('/ticket'), offlineModalScanScreenMessage, true)}
      >
        Ticket
      </Button>
    );
  }

  if (
    (buttons.length < 3 || (buttons.length < 4 && !showOtherButton)) &&
    giftCardEnabled
  ) {
    buttons.push(
      <Button
        className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length+1 ? styles.long : styles.short])}
        onClick={() => checkIfOfflineWithCallback(() => handleScanScreenSelection('/gift-card'), offlineModalScanScreenMessage, true)}
      >
        Gift Card
      </Button>
    );
  }

  if (showOtherButton || (buttons.length < 4 && !isEmpty(noOpPayments))) {
    buttons.push(
      <Button
        className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length+1 ? styles.long : styles.short])}
        onClick={() => history.push("/tender-selection")}
      >
        Other
      </Button>
    );
  }

  buttons.push(
    <Button
      className={cn(styles.checkout, styles.long)}
      onClick={() => checkIfOfflineWithCallback(() => setShowSplitPayDialog(true), 'Sorry, this device is now offline. Please\nchoose another method of payment.', true)}
    >
      Split
    </Button>
  );

  let modal = null;

  if (showSplitPayDialog) {
    modal = (
      <ConfirmSplitPaymentDialog
        onClose={() => setShowSplitPayDialog(false)}
        fromConcessions={true}
      />
    );
  }

  const Cart = () => (
    <>
      <SidebarModal
        show={showCart}
        title="New Order"
        className={styles.cart}
        onClose={() => setShowCart(false)}
        isShowClear={true}
        clearCart={clearItemsAndPromotions}
      >
        <div className={styles.bodyContainer}>
          <div className={styles.scrollable}>
            <CartItems
              className={styles.cartItems}
              discountsAvailable={discountsAvailable}
              networkAvailable={networkAvailable}
            />
          </div>
          <div className={styles.buttonContainer}>{buttons}</div>
        </div>
      </SidebarModal>
    </>
  );

  return (
    hasCartItems && (
      <div className={classNames}>
        <Button
          className={styles.showCartButton}
          onClick={() => setShowCart(true)}
        >
          <span className={styles.quantity}>{itemCount}</span>
          <span className={styles.title}>View Cart</span>
          <span className={styles.total}>{total}</span>
        </Button>
        <Cart />
        {modal}
        {offlineModal}
      </div>
    )
  );
};

export default withRouter(CartModal);
